// ============================================================
//  Catalog loader — bundles the canonical materials/labor/equipment
//  catalog snapshot at build time.
//
//  Mirrors the v3 catalog_items table in Supabase. The snapshot at
//  /documents/categories/_catalog-snapshot.json is refreshed when
//  the materials research chat lands new batches; rebuild the app
//  to pick up changes.
//
//  Templates reference catalog rows via `pricebookItemHint` strings
//  that match `catalog_items.id` exactly. The loader resolves a hint
//  to a CatalogItem so the proposal editor can:
//    - Pre-fill `name` / `unit` / `default_kind` for unconfigured rows
//    - Surface an "unresolved hint" warning when a template references
//      a slug not in the catalog
// ============================================================

export type CatalogKind = 'material' | 'labor' | 'equipment_rental' | 'equipment_owned';

export interface CatalogItem {
  id: string;             // e.g. "landscaping.compost-cu-yd"
  industry: string;       // e.g. "landscaping"
  category: string;       // e.g. "softscape"
  subcategory: string | null;
  name: string;
  unit: string;
  default_kind: CatalogKind;
  archived_at?: string | null;
}

// Vite glob — eager inlines the JSON at build time. Single file expected.
const SNAPSHOT_FILES = import.meta.glob(
  '/documents/categories/_catalog-snapshot.json',
  { eager: true, import: 'default' }
) as Record<string, CatalogItem[]>;

const SNAPSHOT: CatalogItem[] = (() => {
  const entries = Object.values(SNAPSHOT_FILES);
  if (entries.length === 0) {
    console.warn('[catalogLoader] No catalog snapshot found. Templates will not resolve.');
    return [];
  }
  return entries[0].filter(c => !c.archived_at);
})();

const SNAPSHOT_BY_ID = new Map<string, CatalogItem>();
for (const c of SNAPSHOT) SNAPSHOT_BY_ID.set(c.id, c);

/** Full catalog snapshot (active rows only — archived ones excluded). */
export function loadCatalog(): CatalogItem[] {
  return SNAPSHOT;
}

/** Catalog scoped to one industry. Most callers want this. */
export function loadCatalogByIndustry(industry: string): CatalogItem[] {
  return SNAPSHOT.filter(c => c.industry === industry);
}

/** Catalog scoped to one industry + category combination. */
export function loadCatalogByCategory(industry: string, category: string): CatalogItem[] {
  return SNAPSHOT.filter(c => c.industry === industry && c.category === category);
}

/** Look up a single catalog item by id. Returns null if not in the snapshot. */
export function lookupCatalogItem(id: string): CatalogItem | null {
  return SNAPSHOT_BY_ID.get(id) ?? null;
}

/** True if a hint resolves to an active catalog row. Used by the validator. */
export function hintResolves(hint: string | undefined | null): boolean {
  return !!hint && SNAPSHOT_BY_ID.has(hint);
}

/** Catalog row count by industry — useful for dev sanity / status panels. */
export function catalogStats(): { total: number; byIndustry: Record<string, number> } {
  const byIndustry: Record<string, number> = {};
  for (const c of SNAPSHOT) {
    byIndustry[c.industry] = (byIndustry[c.industry] ?? 0) + 1;
  }
  return { total: SNAPSHOT.length, byIndustry };
}
